// src/components/TxtRayDiffViewer.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Eye, EyeOff, Layers, Split, GitCommit, 
  Search, ArrowUp, ArrowDown, X,
  Maximize2, Minimize2, ZoomIn, ZoomOut
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Diff from 'diff';
import './TxtRayDiffViewer.css';

interface TxtRayDiffViewerProps {
  text1: string;
  text2: string;
  title1?: string;
  title2?: string;
  onClose?: () => void;
  className?: string;
}

interface DiffLine {
  equal?: boolean;
  added?: boolean;
  removed?: boolean;
  line1?: string;
  line2?: string;
  lineNum?: number;
  lineNum2?: number;
}

const TxtRayDiffViewer: React.FC<TxtRayDiffViewerProps> = ({
  text1,
  text2,
  title1 = 'Original',
  title2 = 'Modified',
  onClose,
  className = ''
}) => {
  const [viewMode, setViewMode] = useState<'unified' | 'overlay' | 'side-by-side'>('side-by-side');
  const [showText1, setShowText1] = useState(true);
  const [showText2, setShowText2] = useState(true);
  const [opacity, setOpacity] = useState(0.7);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wordLevel, setWordLevel] = useState(false);
  const [contextLines] = useState(3);
  const [textSize, setTextSize] = useState(100); // Default 100%
  
  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  
  // Text size adjustment functions
  const increaseTextSize = () => {
    setTextSize(prev => Math.min(prev + 10, 150)); // Max 150%
  };
  
  const decreaseTextSize = () => {
    setTextSize(prev => Math.max(prev - 10, 50)); // Min 50%
  };
  
  const resetTextSize = () => {
    setTextSize(100);
  };
  
  // Compute diff
  const diffResult = useMemo(() => {
    // Split texts into lines first
    const lines1 = text1.split('\n');
    const lines2 = text2.split('\n');
    
    // Use diffArrays to compare line by line
    const diff = Diff.diffArrays(lines1, lines2);
    
    const result: DiffLine[] = [];
    let lineNum1 = 1;
    let lineNum2 = 1;
    
    // Process diff parts and merge consecutive removed/added pairs
    for (let i = 0; i < diff.length; i++) {
      const part = diff[i];
      const nextPart = diff[i + 1];
      
      // Check if this is a removed part followed by an added part
      // This represents changed lines that should be shown side-by-side
      if (part.removed && nextPart?.added) {
        const removedLines = part.value as string[];
        const addedLines = nextPart.value as string[];
        const maxLength = Math.max(removedLines.length, addedLines.length);
        
        for (let j = 0; j < maxLength; j++) {
          result.push({
            removed: j < removedLines.length,
            added: j < addedLines.length,
            line1: j < removedLines.length ? removedLines[j] : undefined,
            line2: j < addedLines.length ? addedLines[j] : undefined,
            lineNum: j < removedLines.length ? lineNum1++ : undefined,
            lineNum2: j < addedLines.length ? lineNum2++ : undefined
          });
        }
        
        // Skip the next part since we've already processed it
        i++;
      } else if (part.added) {
        // Standalone added lines
        const lines = part.value as string[];
        lines.forEach(line => {
          result.push({
            added: true,
            line2: line,
            lineNum2: lineNum2++
          });
        });
      } else if (part.removed) {
        // Standalone removed lines
        const lines = part.value as string[];
        lines.forEach(line => {
          result.push({
            removed: true,
            line1: line,
            lineNum: lineNum1++
          });
        });
      } else {
        // Equal lines
        const lines = part.value as string[];
        lines.forEach(line => {
          result.push({
            equal: true,
            line1: line,
            line2: line,
            lineNum: lineNum1++,
            lineNum2: lineNum2++
          });
        });
      }
    }
    
    return { lines: result, hunks: [] };
  }, [text1, text2, wordLevel, contextLines]);
  
  // Search functionality
  useEffect(() => {
    if (searchTerm) {
      const results: number[] = [];
      diffResult.lines.forEach((line, idx) => {
        const textToSearch = line.line1 || line.line2 || '';
        if (textToSearch.toLowerCase().includes(searchTerm.toLowerCase())) {
          results.push(idx);
        }
      });
      setSearchResults(results);
      setCurrentSearchIndex(0);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, diffResult]);
  
  const goToNextSearchResult = useCallback(() => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
    }
  }, [searchResults]);
  
  const goToPreviousSearchResult = useCallback(() => {
    if (searchResults.length > 0) {
      setCurrentSearchIndex((prev) => 
        prev === 0 ? searchResults.length - 1 : prev - 1
      );
    }
  }, [searchResults]);
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'f') {
          e.preventDefault();
          setShowSearch(prev => !prev);
        }
        // Text size shortcuts
        if (e.key === '+' || e.key === '=') {
          e.preventDefault();
          increaseTextSize();
        }
        if (e.key === '-' || e.key === '_') {
          e.preventDefault();
          decreaseTextSize();
        }
        if (e.key === '0') {
          e.preventDefault();
          resetTextSize();
        }
      }
      if (showSearch) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (e.shiftKey) {
            goToPreviousSearchResult();
          } else {
            goToNextSearchResult();
          }
        }
        if (e.key === 'Escape') {
          setShowSearch(false);
          setSearchTerm('');
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, goToNextSearchResult, goToPreviousSearchResult, increaseTextSize, decreaseTextSize, resetTextSize]);
  
  const highlightSearchTerm = (text: string) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return (
      <>
        {parts.map((part, idx) => 
          regex.test(part) ? (
            <span key={idx} className="search-highlight">
              {part}
            </span>
          ) : (
            <span key={idx}>{part}</span>
          )
        )}
      </>
    );
  };
  
  const renderUnifiedView = () => {
    const unifiedLines: any[] = [];
    
    // Expand combined entries for unified view
    diffResult.lines.forEach((line, idx) => {
      if (line.removed && line.added) {
        // This is a changed line - show both removed and added
        unifiedLines.push({
          ...line,
          type: 'removed',
          displayLine: line.line1,
          displayLineNum: line.lineNum,
          originalIdx: idx
        });
        unifiedLines.push({
          ...line,
          type: 'added',
          displayLine: line.line2,
          displayLineNum: line.lineNum2,
          originalIdx: idx
        });
      } else if (line.removed) {
        unifiedLines.push({
          ...line,
          type: 'removed',
          displayLine: line.line1,
          displayLineNum: line.lineNum,
          originalIdx: idx
        });
      } else if (line.added) {
        unifiedLines.push({
          ...line,
          type: 'added',
          displayLine: line.line2,
          displayLineNum: line.lineNum2,
          originalIdx: idx
        });
      } else {
        unifiedLines.push({
          ...line,
          type: 'unchanged',
          displayLine: line.line1 || line.line2,
          displayLineNum: line.lineNum || line.lineNum2,
          originalIdx: idx
        });
      }
    });
    
    return (
      <div className="unified-view">
        {unifiedLines.map((line, idx) => {
          const isSearchResult = searchResults.includes(line.originalIdx);
          const isCurrentSearch = isSearchResult && searchResults[currentSearchIndex] === line.originalIdx;
          
          return (
            <div
              key={idx}
              className={`diff-line ${line.type === 'added' ? 'added' : ''} ${line.type === 'removed' ? 'removed' : ''} ${isSearchResult ? 'search-result' : ''} ${isCurrentSearch ? 'current-search' : ''}`}
            >
              <span className="line-number">
                {line.displayLineNum}
              </span>
              <span className={`line-marker ${line.type === 'added' ? 'added' : line.type === 'removed' ? 'removed' : ''}`}>
                {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
              </span>
              <span className={`line-content ${line.type === 'added' ? 'added' : line.type === 'removed' ? 'removed' : 'unchanged'}`}>
                {highlightSearchTerm(line.displayLine || '')}
              </span>
            </div>
          );
        })}
      </div>
    );
  };
  
  const renderOverlayView = () => {
    // Helper to render line with only specific changes highlighted
    const renderLineWithHighlights = (line1: string | undefined, line2: string | undefined, showText: 'text1' | 'text2') => {
      if (!line1 || !line2) return null;
      
      // Use character-level diff for precise highlighting
      const charDiff = Diff.diffChars(line1, line2);
      
      if (showText === 'text1') {
        // For text1, show the original text but highlight removed parts
        return charDiff.map((part, idx) => {
          if (part.removed) {
            return <span key={idx} className="overlay-highlight-removed-inline">{part.value}</span>;
          } else if (!part.added) {
            return <span key={idx}>{part.value}</span>;
          }
          return null; // Don't show added parts in text1
        });
      } else {
        // For text2, show the new text but highlight added parts
        return charDiff.map((part, idx) => {
          if (part.added) {
            return <span key={idx} className="overlay-highlight-added-inline">{part.value}</span>;
          } else if (!part.removed) {
            return <span key={idx}>{part.value}</span>;
          }
          return null; // Don't show removed parts in text2
        });
      }
    };

    return (
      <div className="overlay-view">
        {/* Original text layer */}
        {showText1 && (
          <div className="overlay-layer overlay-text1" style={{ opacity }}>
            {diffResult.lines.map((line, idx) => (
              <div key={`t1-${idx}`} className="overlay-line">
                {line.removed && line.added && line.line1 && line.line2 ? (
                  // Changed line - highlight only removed parts
                  renderLineWithHighlights(line.line1, line.line2, 'text1')
                ) : (line.removed || line.equal) && line.line1 ? (
                  // Removed or unchanged line
                  <span className={line.removed && !line.added ? 'overlay-removed' : ''}>
                    {line.line1}
                  </span>
                ) : (
                  // Placeholder for added lines
                  <span className="overlay-spacer">&nbsp;</span>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Modified text layer */}
        {showText2 && (
          <div className="overlay-layer overlay-text2" style={{ opacity: 0.5 }}>
            {diffResult.lines.map((line, idx) => (
              <div key={`t2-${idx}`} className="overlay-line">
                {line.removed && line.added && line.line1 && line.line2 ? (
                  // Changed line - highlight only added parts
                  renderLineWithHighlights(line.line1, line.line2, 'text2')
                ) : (line.added || line.equal) && line.line2 ? (
                  // Added or unchanged line
                  <span className={line.added && !line.removed ? 'overlay-added' : ''}>
                    {line.line2}
                  </span>
                ) : (
                  // Placeholder for removed lines
                  <span className="overlay-spacer">&nbsp;</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  const renderSideBySideView = () => {
    return (
      <div className="side-by-side-container">
        <div className="side-by-side-headers">
          <h3 className="pane-header">{title1}</h3>
          <h3 className="pane-header">{title2}</h3>
        </div>
        <div className="side-by-side-view">
          <div className="diff-pane">
            <div className="pane-content">
              {diffResult.lines.map((line, idx) => (
                <div
                  key={idx}
                  className={`side-line ${
                    line.removed && line.line1 ? 'removed' : 
                    line.line1 ? 'unchanged' : 
                    'invisible'
                  }`}
                >
                  <span className="line-number">
                    {line.lineNum || ''}
                  </span>
                  <span className="line-content">
                    {line.line1 ? highlightSearchTerm(line.line1) : '\u00A0'}
                  </span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="diff-pane">
            <div className="pane-content">
              {diffResult.lines.map((line, idx) => (
                <div
                  key={idx}
                  className={`side-line ${
                    line.added && line.line2 ? 'added' : 
                    line.line2 ? 'unchanged' : 
                    'invisible'
                  }`}
                >
                  <span className="line-number">
                    {line.lineNum2 || ''}
                  </span>
                  <span className="line-content">
                    {line.line2 ? highlightSearchTerm(line.line2) : '\u00A0'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const stats = useMemo(() => {
    const added = diffResult.lines.filter(l => l.added).length;
    const removed = diffResult.lines.filter(l => l.removed).length;
    const modified = Math.min(added, removed);
    return { added: added - modified, removed: removed - modified, modified };
  }, [diffResult]);
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className={`txt-ray-diff-viewer ${isFullscreen ? 'fullscreen' : ''} ${className}`}
      >
        {/* Header */}
        <div className="diff-viewer-header">
          <div className="diff-header-left">
            <h2 className="diff-title">
              <GitCommit size={20} />
              Document Comparison
            </h2>
            
            {/* View mode toggles */}
            <div className="view-mode-buttons">
              <button
                onClick={() => setViewMode('unified')}
                className={`view-mode-btn ${viewMode === 'unified' ? 'active' : ''}`}
                title="Unified View"
              >
                <Layers size={16} />
                Unified
              </button>
              <button
                onClick={() => setViewMode('side-by-side')}
                className={`view-mode-btn ${viewMode === 'side-by-side' ? 'active' : ''}`}
                title="Side by Side"
              >
                <Split size={16} />
                Side by Side
              </button>
              <button
                onClick={() => setViewMode('overlay')}
                className={`view-mode-btn ${viewMode === 'overlay' ? 'active' : ''}`}
                title="Overlay View"
              >
                <Eye size={16} />
                Overlay
              </button>
            </div>
            
            {/* Stats */}
            <div className="diff-stats">
              <span className="stat-added">+{stats.added}</span>
              <span className="stat-removed">-{stats.removed}</span>
              <span className="stat-modified">~{stats.modified}</span>
            </div>
          </div>
          
          <div className="diff-header-right">
            {/* Search button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={`icon-btn ${showSearch ? 'active' : ''}`}
              title="Search (Ctrl+F)"
            >
              <Search size={18} />
            </button>
            
            {/* Settings */}
            {viewMode === 'overlay' && (
              <div className="opacity-controls">
                <button
                  onClick={() => setShowText1(!showText1)}
                  className={`icon-btn toggle-text1 ${showText1 ? 'active' : ''}`}
                  title="Toggle Text 1 (Original)"
                >
                  {showText1 ? <Eye size={18} /> : <EyeOff size={18} />}
                  <span className="btn-label">Text 1</span>
                </button>
                <div className="opacity-slider-group">
                  <label>Opacity:</label>
                  <input
                    type="range"
                    min="10"
                    max="100"
                    step="10"
                    value={opacity * 100}
                    onChange={(e) => setOpacity(Number(e.target.value) / 100)}
                    className="opacity-slider"
                  />
                  <span className="opacity-value">{Math.round(opacity * 100)}%</span>
                </div>
                <button
                  onClick={() => setShowText2(!showText2)}
                  className={`icon-btn toggle-text2 ${showText2 ? 'active' : ''}`}
                  title="Toggle Text 2 (Modified)"
                >
                  {showText2 ? <Eye size={18} /> : <EyeOff size={18} />}
                  <span className="btn-label">Text 2</span>
                </button>
              </div>
            )}
            
            {/* Fullscreen toggle */}
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="icon-btn"
              title="Toggle Fullscreen"
            >
              {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
            </button>
            
            {/* Close button */}
            {onClose && (
              <button
                onClick={onClose}
                className="icon-btn"
                title="Close"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
        
        {/* Search bar */}
        {showSearch && (
          <div className="diff-search-bar">
            <div className="search-container">
              <div className="search-input-wrapper">
                <Search size={16} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="search-input"
                  autoFocus
                />
                {searchResults.length > 0 && (
                  <span className="search-results-count">
                    {currentSearchIndex + 1} / {searchResults.length}
                  </span>
                )}
              </div>
              <button
                onClick={goToPreviousSearchResult}
                className="search-nav-btn"
                disabled={searchResults.length === 0}
              >
                <ArrowUp size={16} />
              </button>
              <button
                onClick={goToNextSearchResult}
                className="search-nav-btn"
                disabled={searchResults.length === 0}
              >
                <ArrowDown size={16} />
              </button>
            </div>
          </div>
        )}
        
        {/* Content area */}
        <div className="diff-content" style={{ fontSize: `${12 * (textSize / 100)}px` }}>
          {viewMode === 'unified' && renderUnifiedView()}
          {viewMode === 'overlay' && renderOverlayView()}
          {viewMode === 'side-by-side' && renderSideBySideView()}
        </div>
        
        {/* Footer */}
        <div className="diff-footer">
          <div className="footer-left">
            <span>Lines: {diffResult.lines.length}</span>
            <span>Changes: {stats.added + stats.removed + stats.modified}</span>
          </div>
          <div className="footer-center">
            {/* Text size controls */}
            <div className="text-size-controls">
              <button
                onClick={decreaseTextSize}
                className="icon-btn"
                title="Decrease text size (Ctrl/-)"
                disabled={textSize <= 50}
              >
                <ZoomOut size={16} />
              </button>
              <span className="text-size-value">{textSize}%</span>
              <button
                onClick={increaseTextSize}
                className="icon-btn"
                title="Increase text size (Ctrl/+)"
                disabled={textSize >= 150}
              >
                <ZoomIn size={16} />
              </button>
              <button
                onClick={resetTextSize}
                className={`icon-btn reset-btn ${textSize === 100 ? 'disabled' : ''}`}
                title="Reset text size to 100% (Ctrl/0)"
                disabled={textSize === 100}
              >
                Reset
              </button>
            </div>
          </div>
          <div className="footer-right">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={wordLevel}
                onChange={(e) => setWordLevel(e.target.checked)}
              />
              Word-level diff
            </label>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TxtRayDiffViewer;